import React, { useEffect } from 'react';
import {
    Layers,
    MessageSquare,
    Building2,
    CameraOff,
    Filter,
    Wallet,
    Compass,
    FileText,
    Bot,
    Film,
    Calculator,
    Megaphone,
    Workflow,
    LayoutDashboard,
    Video,
    ListChecks,
    Mic,
    PhoneCall,
    CheckCircle2,
    XCircle
} from 'lucide-react';

/* Reusable card: icon + heading + subheading, nothing else */
const InfoCard = ({ icon: Icon, heading, subheading, accent = "indigo" }) => {
    const accents = {
        indigo: {
            bg: "bg-indigo-900/10",
            border: "border-indigo-500/30",
            iconBg: "bg-indigo-500/10",
            iconColor: "text-indigo-300"
        },
        rose: {
            bg: "bg-rose-900/10",
            border: "border-rose-500/30",
            iconBg: "bg-rose-500/10",
            iconColor: "text-rose-300"
        },
        emerald: {
            bg: "bg-emerald-900/10",
            border: "border-emerald-500/30",
            iconBg: "bg-emerald-500/10",
            iconColor: "text-emerald-300"
        }
    };
    const a = accents[accent];
    return (
        <div className={`rounded-2xl border ${a.border} ${a.bg} p-6 sm:p-7 flex flex-col`}>
            <div className={`${a.iconBg} ${a.iconColor} w-11 h-11 rounded-xl flex items-center justify-center mb-5`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-base sm:text-lg font-black text-white mb-2 leading-snug">{heading}</h3>
            <p className={`text-xs sm:text-sm font-medium leading-snug ${accent === 'rose' ? 'text-rose-100/70' : 'text-white/55'}`}>{subheading}</p>
        </div>
    );
};

const SectionEyebrow = ({ children, color = "indigo" }) => {
    const colors = {
        indigo: "text-indigo-300/80",
        rose: "text-rose-300/80",
        amber: "text-amber-300/80",
        emerald: "text-emerald-300/80"
    };
    return (
        <div className={`text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-4 ${colors[color]}`}>
            {children}
        </div>
    );
};

export default function RishiPersonalBrand() {

    useEffect(() => {
        document.title = "Rishi Batra | Buyer-First Content Engine";
        window.scrollTo(0, 0);
    }, []);

    const funnelSteps = [
        { n: '01', h: 'A video speaks only to the buyer', s: 'Every script opens for the person investing 2 Cr and above. Everyone else swipes away, and that is by design.' },
        { n: '02', h: 'They comment for a resource', s: 'A rental yield calculator, a buying checklist. A free resource becomes a proxy for trust, your words on the call.' },
        { n: '03', h: 'The AI setter qualifies them', s: 'A Meta-verified DM system asks the qualifying questions you already ask by hand. Window shoppers filter themselves out.' },
        { n: '04', h: 'A call lands on your calendar', s: 'You only meet people who answered the questions. Closing stays yours.' }
    ];

    const wont = [
        { h: 'A guarantee number today', s: 'We study the niche and your baseline in week 1, then put a number in writing that we can actually back.' },
        { h: 'Views and followers as the goal', s: 'Vanity reach pulls window shoppers. The metric here is buyer comments, qualified leads and booked calls.' },
        { h: 'Overnight closings', s: 'A 2 Cr buyer moves on trust. The system builds that trust every single day, it does not shortcut it.' }
    ];

    const will = [
        { h: 'A minimum guarantee in writing', s: 'Locked after week 1 research, built on buyer signals. If we miss it, 100% money back.' },
        { h: 'A content engine that runs without you', s: 'Research to posting handled end to end. Your involvement is one studio session and approvals.' },
        { h: 'Qualified leads, not raw DMs', s: 'Every lead is questioned by the setter before it reaches you. Time passers never make the calendar.' }
    ];

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pt-36 sm:pt-40 pb-20 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Rishi Batra</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Private</span>
                </div>
            </header>

            <main className="max-w-[1080px] mx-auto px-6 space-y-28 sm:space-y-40">

                {/* HERO */}
                <section className="text-center pt-12 sm:pt-20">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-10">
                        Confidential · Private Proposal
                    </div>

                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-2">
                        The educated voice<br />buyers are looking for.
                    </h1>

                    <p className="text-base sm:text-lg md:text-xl text-white/55 max-w-2xl mx-auto leading-relaxed mb-16 font-light">
                        A second, dedicated page for Rishi Batra that speaks only to the 2 Cr+ buyer.<br className="hidden sm:block" /> Runs on your AI clone, turns comments into qualified calls, and leaves your 25K persona page untouched.
                    </p>

                    <div className="max-w-md mx-auto">
                        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm px-6 py-8">
                            <div className="text-[10px] sm:text-xs text-white/40 font-bold tracking-widest uppercase mb-3">Investment · Monthly</div>
                            <div className="text-4xl sm:text-5xl font-black text-white tracking-tight">₹1.5 Lakh</div>
                            <div className="text-xs sm:text-sm text-white/50 mt-2">3-month minimum · everything included</div>
                        </div>
                    </div>
                </section>

                {/* THE FUNNEL */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The system</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            From a video<br />to a booked call.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            You said it on the call: people buy property from an educated person who has opinions. The page we build turns those opinions into a pipeline.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {funnelSteps.map((step) => (
                            <div key={step.n} className="flex items-start gap-5 sm:gap-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
                                <div className="text-2xl sm:text-3xl font-black text-indigo-400/80 shrink-0 w-10 sm:w-12 tabular-nums">{step.n}</div>
                                <div>
                                    <h3 className="text-base sm:text-lg font-black text-white mb-1 leading-snug">{step.h}</h3>
                                    <p className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{step.s}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* PAIN POINTS */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="rose">The honest read</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Where you are right now.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Layers} heading="One page, mixed signals" subheading="25K followers built on urban India opinions · buyer content would muddy it, and you know it" accent="rose" />
                        <InfoCard icon={MessageSquare} heading="DMs full of window shoppers" subheading="Leads ask one question and disappear · nothing qualifies them, nothing creates urgency" accent="rose" />
                        <InfoCard icon={Building2} heading="Leads from the same portals" subheading="99acres, MagicBricks, references · the channels every consultant in Gurgaon runs on" accent="rose" />
                        <InfoCard icon={CameraOff} heading="Content needs your camera time" subheading="Raw talking videos work for persona · daily polished buyer content is not possible solo" accent="rose" />
                        <InfoCard icon={Filter} heading="Curiosity without a funnel" subheading="The page creates DMs · nothing is built to turn them into booked calls" accent="rose" />
                        <InfoCard icon={Wallet} heading="Attention not yet monetized" subheading="Calls happen, money does not move · the 2 Cr buyer needs a different pipeline" accent="rose" />
                    </div>
                </section>

                {/* WHAT WE BUILD */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">Our side</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we build in 90 days.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-6xl mx-auto">
                        <InfoCard icon={Compass} heading="A second, dedicated page" subheading="Speaks only to the 2 Cr+ buyer and the fraternity · your current page keeps running" accent="indigo" />
                        <InfoCard icon={FileText} heading="Complete done for you" subheading="Research, ideation, scripting, editing, posting, management" accent="indigo" />
                        <InfoCard icon={Bot} heading="Your AI clone" subheading="One studio session · then videos ship without you recording again" accent="indigo" />
                        <InfoCard icon={Film} heading="Volume your calendar cannot give" subheading="Up to 2 polished videos a day when we push" accent="indigo" />
                        <InfoCard icon={Calculator} heading="Buyer-first scripts" subheading="Rental yield math, buy-or-wait calls · opinions only an educated consultant can give" accent="indigo" />
                        <InfoCard icon={Megaphone} heading="Cross-posted distribution" subheading="Instagram + YouTube Shorts · one system across short form" accent="indigo" />
                        <InfoCard icon={Workflow} heading="Comment-to-resource funnel" subheading="Free calculators and checklists · every comment is a buyer raising a hand" accent="indigo" />
                        <InfoCard icon={LayoutDashboard} heading="AI setter + live dashboard" subheading="Meta-verified DM system qualifies leads, books calls, tracks the pipeline" accent="indigo" />
                    </div>
                </section>

                {/* YOUR SIDE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">Your side</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we need from you.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            Lighter than you think. The clone carries the volume, so you are never chained to a shoot.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Video} heading="One studio session" subheading="Trains the clone · a few outfit changes cover months of videos" accent="emerald" />
                        <InfoCard icon={ListChecks} heading="Week 1 sign-off" subheading="ICP, positioning and content pillars locked with you" accent="emerald" />
                        <InfoCard icon={Mic} heading="Your opinions" subheading="Market calls and deal stories · a voice note is enough, we script from there" accent="emerald" />
                        <InfoCard icon={PhoneCall} heading="Take the qualified calls" subheading="The system fills the calendar · closing stays yours" accent="emerald" />
                    </div>
                </section>

                {/* PROMISE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">The exchange</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we promise.<br />And what we won't.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl mx-auto">
                        {/* WON'T */}
                        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 sm:p-9">
                            <div className="text-rose-300/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-7">What we won't promise</div>
                            <ul className="space-y-6">
                                {wont.map((item) => (
                                    <li key={item.h} className="flex items-start gap-3.5">
                                        <XCircle className="w-5 h-5 text-rose-400/70 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-sm sm:text-base font-bold text-white leading-snug mb-1">{item.h}</div>
                                            <div className="text-xs sm:text-sm text-white/50 font-medium leading-snug">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* WILL */}
                        <div className="rounded-3xl border border-indigo-500/30 bg-indigo-900/10 p-7 sm:p-9">
                            <div className="text-indigo-300/90 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-7">What we do promise</div>
                            <ul className="space-y-6">
                                {will.map((item) => (
                                    <li key={item.h} className="flex items-start gap-3.5">
                                        <CheckCircle2 className="w-5 h-5 text-indigo-300 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-sm sm:text-base font-bold text-white leading-snug mb-1">{item.h}</div>
                                            <div className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* FINAL CTA */}
                <section className="text-center pt-4 sm:pt-8">
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight mb-6">
                        Ready when you are.
                    </h2>
                    <p className="text-base sm:text-lg text-white/55 max-w-xl mx-auto font-light leading-relaxed mb-10">
                        Reply on Instagram where we already talk, or write to us. Week 1 research starts the day you confirm.
                    </p>
                    <a
                        href="mailto:suresh@socialseo.in"
                        className="inline-block bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold px-8 py-4 rounded-full text-sm sm:text-base"
                    >
                        suresh@socialseo.in
                    </a>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-20 border-t border-white/5">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
